import React, { FC, useCallback, useEffect, useState } from 'react'
import { Dialog } from 'primereact/dialog'
import { useNavigate } from 'react-router-dom'
import { useToast } from '../../../common/toast/hooks/useToast'
import { MainContacts, MainContactsFormProps } from './MainContacts.d'
import { Card } from 'primereact/card'
import { Card as RBCard } from 'react-bootstrap'
import useAxiosPrivate from '../../../../hooks/useAxiosPrivate'
import Common from '../../../common/Common'
import { Button, Alert, Row, Col } from 'react-bootstrap'
import { useAppSelector } from '../../../../store/hooks'
import { Formik, Form as FormikForm } from 'formik'
import * as Yup from 'yup'
import RenderFieldsErrors from '../../../common/forms/errors/RenderFieldsErrors'
import { handleAxiosErrors } from '../../../common/forms/errors/Helper'
import FormActions from '../../../common/forms/FormActions'
import { v4 as uuidv4 } from 'uuid'
import MainContactField from './MainContactField'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleNotch, faPlus } from '@fortawesome/free-solid-svg-icons'

const MainContactsForm: FC<MainContactsFormProps> = ({
    inPopup = true,
    config = {},
}) => {
    const theme = useAppSelector((state) => state.theme.theme)

    const navigate = useNavigate()
    const axiosPrivate = useAxiosPrivate()

    const emptyMainContacts: MainContacts = {
        emails: [],
        addresses: [],
        phones: [],
    }

    const MainContactsFormSchema = Yup.object().shape({})

    const [mainContactsDialog, setMainContactsDialog] = useState(true)
    const [mainContacts, setMainContacts] =
        useState<MainContacts>(emptyMainContacts)
    const [originalMainContacts, setOriginalMainContacts] = useState<[]>([])
    const [initialMainContacts, setInitialMainContacts] = useState<[]>([])
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [errMsg, setErrMsg] = useState('')
    const [errorFields, setErrorFields] = useState<any>({})

    const { triggerToast } = useToast()

    const loadPage = useCallback(async () => {
        setErrMsg('')
        setErrorFields({})
        setIsLoading(true)

        try {
            const response = await axiosPrivate
                .get('/administration/main-contacts')
                .finally(() => {
                    setIsLoading(false)
                })
            if (response.data?.success === true) {
                if (response.data.data.main_contacts) {
                    setInitialMainContacts(response.data.data.main_contacts)
                    setOriginalMainContacts(response.data.data.main_contacts)
                    let _mainContacts: MainContacts = {}
                    for (const i in response.data.data.main_contacts) {
                        const typeValues = response.data.data.main_contacts[i]
                        for (const x in typeValues) {
                            _mainContacts[`${i}-${typeValues[x]['id']}`] =
                                typeValues[x]['value']
                        }
                    }
                    setMainContacts(_mainContacts)
                }
            } else {
                setErrMsg(response.data?.errors.join('<br />'))
            }
        } catch (err) {
            // TODO: Sort out error handling!
            if (!err?.response) {
                setErrMsg('No Server Response')
            } else if (err.response?.status === 404) {
                setErrMsg('Record was not found')
            } else {
                setErrMsg('Failed to load main contacts')
            }
        }
    }, [axiosPrivate])

    useEffect(() => {
        loadPage()
    }, [loadPage])

    const saveRecord = async (record) => {
        setErrMsg('')
        setIsSubmitting(true)

        delete record['main_contacts']

        try {
            const response = await axiosPrivate
                .post('/administration/main-contacts/update', record, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                })
                .finally(() => {
                    setIsSubmitting(false)
                })

            if (response.data?.success === true) {
                triggerToast({
                    severity: 'success',
                    summary: config?.submitSuccessMsg?.header || 'Successful',
                    detail:
                        config?.submitSuccessMsg?.message ||
                        'Processed successfully',
                })
                if (inPopup) hideDialog()
            } else {
                setErrMsg(response.data?.errors.join('<br />'))
            }
        } catch (err) {
            handleAxiosErrors(err, setErrMsg, setErrorFields, config)
        }
    }

    const hideDialog = () => {
        setMainContactsDialog(false)
        navigate('/administration')
    }

    const getTypeConfig = (type: string) => {
        switch (type) {
            case 'emails':
                return {
                    type: 'email',
                    label: 'Emails',
                    singleLabel: 'Email',
                }
            case 'addresses':
                return {
                    type: 'textarea',
                    label: 'Addresses',
                    singleLabel: 'Address',
                }
            case 'phones':
                return {
                    type: 'text',
                    label: 'Phones',
                    singleLabel: 'Phone',
                }
            default:
                return {}
        }
    }

    // useEffect(() => {}, [originalMainContacts])

    const FormFooter = (
        <React.Fragment>
            <Button
                variant={theme === 'dark' ? 'dark' : 'light'}
                onClick={hideDialog}
            >
                <span className="small">Cancel</span>
            </Button>
            <Button
                variant={theme === 'dark' ? 'dark' : 'light'}
                onClick={() => {
                    const formResetBtn = document.getElementById(
                        'mainContacts-form-reset-btn'
                    )
                    formResetBtn.click()
                }}
            >
                <span className="small">Reset</span>
            </Button>
            <Button
                variant="success"
                disabled={isSubmitting || isLoading}
                onClick={() => {
                    const formSubmitGtn = document.getElementById(
                        'mainContacts-form-submit-btn'
                    )
                    formSubmitGtn.click()
                }}
            >
                <span className="small">
                    {isSubmitting ? (
                        <>
                            <FontAwesomeIcon icon={faCircleNotch} spin={true} />{' '}
                            {config?.saveButton?.savingLabel || 'Processing...'}
                        </>
                    ) : (
                        config?.saveButton?.label || 'Process'
                    )}
                </span>
            </Button>
        </React.Fragment>
    )

    const FormContent = (
        <>
            {isLoading && <Common.Loader text="Loading..." />}
            {errMsg && (
                <Alert show={true} variant="danger">
                    <Alert.Heading className="d-inline-block me-2">
                        Error!
                    </Alert.Heading>
                    <RenderFieldsErrors
                        errorFields={errorFields}
                        errMsg={errMsg}
                    />
                </Alert>
            )}
            <Formik
                initialValues={{
                    ...mainContacts,
                }}
                validationSchema={MainContactsFormSchema}
                onSubmit={(values) => {
                    saveRecord(values)
                }}
                enableReinitialize={true}
                onReset={(e, props) => {
                    const { setFieldValue } = props
                    setOriginalMainContacts(initialMainContacts)
                    for (const i in initialMainContacts) {
                        const typeValues: any = initialMainContacts[i]
                        for (const x in typeValues) {
                            setFieldValue(
                                `${i}-${typeValues[x]['id']}`,
                                typeValues[x]['value']
                            )
                        }
                    }
                }}
            >
                {(formikProps) => {
                    return (
                        <FormikForm>
                            <Row className="mb-3">
                                {Object.keys(originalMainContacts).map(
                                    (type: string) => {
                                        const config = getTypeConfig(type)
                                        return (
                                            <Col key={type} xl={6} xxl={4}>
                                                <RBCard className="shadow mb-3">
                                                    <RBCard.Header
                                                        className={`bg-${
                                                            theme === 'dark'
                                                                ? 'dark'
                                                                : 'light'
                                                        }`}
                                                    >
                                                        <div
                                                            className={`float-start text-${
                                                                theme === 'dark'
                                                                    ? 'light'
                                                                    : 'dark'
                                                            }`}
                                                        >
                                                            {config.label}
                                                        </div>
                                                        <Button
                                                            variant={`outline-${
                                                                theme === 'dark'
                                                                    ? 'info'
                                                                    : 'primary'
                                                            }`}
                                                            className="float-end"
                                                            size="sm"
                                                            onClick={() => {
                                                                setOriginalMainContacts(
                                                                    (
                                                                        prevState
                                                                    ) => {
                                                                        return {
                                                                            ...prevState,
                                                                            [type]: [
                                                                                ...prevState[
                                                                                    type
                                                                                ],
                                                                                {
                                                                                    id:
                                                                                        'new-' +
                                                                                        uuidv4(),
                                                                                    value: '',
                                                                                },
                                                                            ],
                                                                        }
                                                                    }
                                                                )
                                                            }}
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={faPlus}
                                                                className="me-2"
                                                            />
                                                            New{' '}
                                                            {config.singleLabel}
                                                        </Button>
                                                    </RBCard.Header>
                                                    <RBCard.Body>
                                                        <Row>
                                                            <Col xl={12}>
                                                                {originalMainContacts[
                                                                    type
                                                                ].length > 0 ? (
                                                                    originalMainContacts[
                                                                        type
                                                                    ].map(
                                                                        (
                                                                            field: any
                                                                        ) => (
                                                                            <MainContactField
                                                                                key={
                                                                                    field.id
                                                                                }
                                                                                field={
                                                                                    field
                                                                                }
                                                                                type={
                                                                                    type
                                                                                }
                                                                                config={
                                                                                    config
                                                                                }
                                                                                formikProps={
                                                                                    formikProps
                                                                                }
                                                                                mainContacts={
                                                                                    mainContacts
                                                                                }
                                                                                setMainContacts={
                                                                                    setMainContacts
                                                                                }
                                                                                originalMainContacts={
                                                                                    originalMainContacts
                                                                                }
                                                                                setOriginalMainContacts={
                                                                                    setOriginalMainContacts
                                                                                }
                                                                            />
                                                                        )
                                                                    )
                                                                ) : (
                                                                    <h6 className="text-center p-3">
                                                                        No{' '}
                                                                        {
                                                                            config.singleLabel
                                                                        }
                                                                        (s)
                                                                        found!
                                                                    </h6>
                                                                )}
                                                            </Col>
                                                        </Row>
                                                    </RBCard.Body>
                                                </RBCard>
                                            </Col>
                                        )
                                    }
                                )}
                                <input
                                    type="submit"
                                    id="mainContacts-form-submit-btn"
                                    className="d-none"
                                />
                                <input
                                    type="reset"
                                    id="mainContacts-form-reset-btn"
                                    className="d-none"
                                />
                            </Row>
                        </FormikForm>
                    )
                }}
            </Formik>
        </>
    )

    return (
        <>
            {inPopup ? (
                <Card className="flex justify-content-center">
                    <Dialog
                        visible={mainContactsDialog}
                        breakpoints={{ '960px': '100vw', '640px': '100vw' }}
                        style={{ width: '50vw' }}
                        header={'Edit main contacts'}
                        modal
                        className="p-fluid"
                        footer={FormFooter}
                        onHide={hideDialog}
                        maximizable
                        contentClassName={isLoading ? 'wrapped-content' : ''}
                        closeOnEscape={false}
                    >
                        {FormContent}
                    </Dialog>
                </Card>
            ) : (
                <>
                    {FormContent}
                    <FormActions formFooter={FormFooter} />
                </>
            )}
        </>
    )
}

export default MainContactsForm
